import { useCallback } from 'react';
import { getParentOrigin } from '@/utils/parentOrigin';
import { logger } from '@/utils/logger';

export interface IframeNotifyOptions {
  message?: string;
  type?: 'tool' | 'prompt' | 'notify' | 'link' | 'intent';
  toolName?: string;
  params?: Record<string, unknown>;
  prompt?: string;
  url?: string;
  intent?: string;
}

/**
 * Hook to send messages to the parent iframe after completing actions.
 * Supports notify messages, tool actions, and field change synchronization
 * for MCP UI components.
 *
 * Returns a callable function with an additional `sendFieldChange` property
 * for bidirectional field sync. This allows backward-compatible usage:
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const sendNotify = useIframeNotify();
 *
 *   // Send a simple notify message (backward compatible)
 *   sendNotify({ message: 'Action completed' });
 *
 *   // Send a tool action
 *   sendNotify({
 *     type: 'tool',
 *     toolName: 'add_proposed_event',
 *     params: { eventId: '123', spaceId: 456, startTime: '...', endTime: '...' },
 *   });
 *
 *   // Send a field change for bidirectional sync
 *   sendNotify.sendFieldChange('title', 'New Title');
 * };
 * ```
 */
export const useIframeNotify = () => {
  const sendNotify = useCallback((options: IframeNotifyOptions) => {
    if (window.parent === window) {
      logger.warn('Not in an iframe, cannot send notify');
      return;
    }

    const messageId = `notify-${crypto.randomUUID()}`;

    // Determine the action type - default to 'notify' for backward compatibility
    const actionType = options.type || 'notify';

    // Build payload based on action type
    const payload: Record<string, unknown> = {};

    if (actionType === 'notify' || !options.type) {
      payload.message = options.message || '';
    } else if (actionType === 'tool') {
      payload.toolName = options.toolName;
      payload.params = options.params;
    } else if (actionType === 'prompt') {
      payload.prompt = options.prompt;
    } else if (actionType === 'link') {
      payload.url = options.url;
    } else if (actionType === 'intent') {
      payload.intent = options.intent;
    }

    window.parent.postMessage(
      {
        type: actionType,
        messageId,
        payload,
      },
      getParentOrigin()
    );

    return messageId;
  }, []);

  /**
   * Send a field change message to the parent iframe for bidirectional sync.
   * This was previously in useIframeMessaging and has been consolidated here.
   */
  const sendFieldChange = useCallback((field: string, value: unknown) => {
    if (window.parent === window) {
      console.warn('[useIframeNotify] Not in an iframe, cannot send field change');
      return;
    }

    window.parent.postMessage(
      {
        type: 'ui-field-change',
        payload: { field, value },
      },
      getParentOrigin()
    );
  }, []);

  // Attach sendFieldChange as a property on sendNotify for backward compatibility.
  // Existing consumers can still call sendNotify({...}) directly.
  // New consumers can call sendNotify.sendFieldChange(field, value)
  // or destructure: const { sendFieldChange } = useIframeNotify();
  return Object.assign(sendNotify, { sendFieldChange });
};
